import React, { useContext, useState, useEffect } from 'react';
import {
  GridApi,
  GridOptions,
  CellEditingStoppedEvent,
} from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';
import { AgGridReact } from 'ag-grid-react';
import { flatMap } from 'lodash-es';
import { formatPimDate } from '@mummssoftware/utils/formatters';
import { riskLevel, bereaveStartDate } from './constants';
import { DateCell, DropDownCell, NoteCountCellRenderer } from './CellRenderer';
import { MessageOverlay } from './MessageOverlay';
import { smallWidgetPatientColumnDefs } from './CGTableColumnDef';
import { CGTableDiv } from '../../components/layout/WrapperDiv';
import { BereavementContext } from '../../context';

interface CGTableProps {
  viewModePrint?: boolean;
  smallWidget?: boolean;
}
const frameworkComponents = {
  tableCell: DateCell,
  dropDownCell: DropDownCell,
  noteCountCellRenderer: NoteCountCellRenderer,
  customLoadingOverlay: MessageOverlay,
};

export const CGTable = ({ viewModePrint, smallWidget }: CGTableProps) => {
  const {
    props: {
      grid: { headerHeight },
    },
  } = useTheme() as any;

  const {
    patient,
    careGiversInfo,
    griefRisks,
    showOverlay,
    readOnly,
    updateCareGiver,
  } = useContext(BereavementContext);
  const duplicate = (x: any) => [{ ...x }, { ...x, fullWidth: true }];
  const cgExpandedData =
    careGiversInfo && careGiversInfo.length
      ? flatMap(careGiversInfo, duplicate)
      : [];
  const cgData = smallWidget ? careGiversInfo : cgExpandedData;

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const startDate = {
    headerName: 'Start',
    field: bereaveStartDate,
    cellClass: 'no-border',
    suppressMenu: true,
    cellRendererFramework: DateCell,
    width: 120,
    suppressSizeToFit: true,
    cellRendererParams: {
      patient,
    },
  };

  const dropDownDef = {
    headerName: 'Risk',
    field: riskLevel,
    // for dropdown to work
    editable: true,
    singleClickEdit: true,
    cellClass: 'no-border',
    cellEditor: 'agRichSelectCellEditor',
    width: 100,
    suppressSizeToFit: true,
    cellRendererFramework: DropDownCell,
    cellEditorParams: {
      cellHeight: 30,
      values: griefRisks,
      cellRendererFramework: DropDownCell,
    },
    suppressMenu: true,
  };
  const expandedWidgetPatientColumnDefs = readOnly
    ? [...smallWidgetPatientColumnDefs, startDate]
    : [...smallWidgetPatientColumnDefs, dropDownDef, startDate];
  let columnDefs;
  if (patient) {
    columnDefs = smallWidget
      ? smallWidgetPatientColumnDefs
      : expandedWidgetPatientColumnDefs;
  }

  const saveRiskLevel = (params: CellEditingStoppedEvent) => {
    const { value, data } = params;
    const {
      id,
      patientId,
      patientNumber,
      personId,
      personNumber,
      rank,
      relationship,
      patientRelationTypes,
      cahps,
      bereavedStartDate,
      notes,
      bereaved,
    } = data;
    const requestBody = {
      id,
      patientId,
      patientNumber,
      personId,
      personNumber,
      rank,
      relationship,
      patientRelationTypes: JSON.parse(patientRelationTypes),
      griefRisk: value.griefrisk,
      bereaved,
      cahps,
      bereavedStartDate: formatPimDate(bereavedStartDate),
      notes,
    };
    updateCareGiver(patientId, id, requestBody);
  };

  const onCellEditingStopped = (params: CellEditingStoppedEvent) => {
    const { field } = params.colDef;
    const { value, data } = params;
    if (field === riskLevel) {
      if (value?.griefRisk !== data?.griefRisk) {
        saveRiskLevel(params);
      }
    }
  };

  /**
   * overlay for saving and deleting API calls
   */
  const showTableOverlay = async () => {
    gridApi && gridApi.showLoadingOverlay();
  };
  const hideTableOverlay = async () => {
    gridApi && gridApi.hideOverlay();
  };

  useEffect(() => {
    if (gridApi) {
      if (showOverlay) {
        showTableOverlay();
      }
      if (!showOverlay) {
        hideTableOverlay();
      }
    }
  }, [showOverlay, gridApi]);

  const parseList = (list: any) => {
    const listArray = list?.split('\n');
    const compiledList = listArray.reduce(
      (acc: Array<string>, val: string, index: number) => {
        if (listArray.length === 1) {
          return ['', [`${val}`]];
        }
        if (acc[0] === '') {
          // the start of a column
          acc[0] = `${val}`;
          return acc;
          // if it is the last in the list or colum...
        } else if (
          index === listArray.length - 1 ||
          acc[0].split('\n').length > 5
        ) {
          acc[1] = acc[1].concat(`${acc[0]}\n${val}`);
          acc[0] = '';
          return acc;
        }
        // else append to the bottom of the column
        acc[0] = `${acc[0]}\n${val}`;
        return acc;
      },
      // 0 index is a string to hold columns, 1 index is the array of columns
      ['', []],
    );
    const listMap = compiledList[1]?.reduce(
      (acc: string, val: string, index: number) =>
        `${acc}<div style="width: 220px; align-items: center; line-height: 22px; white-space: pre-wrap;">${val}</div>`,
      '',
    );
    return listMap;
  };

  const fullWidthCellRenderer = React.useMemo(
    () => (params: any) => {
      const text = params?.data?.griefriskactions || '';
      const eDiv = document.createElement('div');
      eDiv.innerHTML = `<div style="display: flex; flex-wrap: wrap; justify-content: left; padding-left: 25px; overflow: scroll">
      ${parseList(text)}
      </div>`;
      return eDiv.innerHTML;
    },
    [],
  );

  const getFullWidthCellHeight = (list: string) => {
    const listLength = list.split('\n').length;

    // 7 risk actions per column * 22 in height per action, or 0 for an empty list
    const height = listLength ? ((listLength < 7 && listLength) || 7) * 22 : 0;
    return height;
  };

  const gridOptions: GridOptions = {
    embedFullWidthRows: true,
    defaultColDef: {
      resizable: true,
      filter: true,
    },
    columnDefs,
    isFullWidthCell(rowNode) {
      return rowNode.data.fullWidth;
    },
    onGridReady: (params: any) => {
      const gridAPI: GridApi = params.api;
      setGridApi(gridAPI);
      gridAPI.sizeColumnsToFit();
      // @strategy for PRINT to work!
      if (gridAPI) {
        if (viewModePrint) {
          gridAPI.setDomLayout('print');
        }
      }
    },
    // eslint-disable-next-line consistent-return
    getRowHeight: (params: any) => {
      const { data } = params;

      if (data.fullWidth) {
        if (data.griefriskactions) {
          return getFullWidthCellHeight(data?.griefriskactions);
        }
        return null;
      }
      return 27;
    },
  };

  return (
    <CGTableDiv smallWidget={smallWidget}>
      <AgGridReact
        gridOptions={gridOptions}
        rowData={cgData || []}
        headerHeight={headerHeight}
        frameworkComponents={frameworkComponents}
        fullWidthCellRenderer={fullWidthCellRenderer}
        onCellEditingStopped={onCellEditingStopped}
        loadingOverlayComponent="customLoadingOverlay"
      />
    </CGTableDiv>
  );
};

export default CGTable;
